import { ErrorFallback, withErrorBoundary } from '@/components/error-fallback';
import { useAuthInfo } from '@propelauth/react';
import * as Sentry from '@sentry/react';
import React, { lazy, useEffect } from 'react';
import { Navigate, Route, BrowserRouter as Router, Routes, useParams } from 'react-router-dom';

import { AuthProvider } from '@/context/auth-context';
import { ReportsVdaOrgRedirect, ReportsVdaRedirect } from '@/app/vda-legacy-redirects';
import { FilingsProvider } from '@/context/filing/filing-provider';
import { PrivacyProvider } from '@/context/privacy-context';
import { RegistrationsProvider } from '@/context/registrations/registration-provider';
import { storeApiToken } from '@/utils/api';
import { isCsmWorkspaceEnabled, isKintsugiTasksBoardEnabled, isOrgHealthCheckEnabled } from '@/utils/feature-flags';
import { AppLayout } from './app-layout';

// Utility function to retry dynamic imports with exponential backoff
const retryImport = (importFn: () => Promise<any>, retries = 3, delay = 1000) => {
  return importFn().catch(error => {
    if (retries === 0) {
      throw error;
    }
    return new Promise(resolve => {
      setTimeout(() => {
        resolve(retryImport(importFn, retries - 1, delay * 2));
      }, delay);
    });
  });
};

const KintsugiFilingsPage = lazy(() => retryImport(() => import('../pages/kintsugi-filings')));
const KintsugiRegistrationsPage = lazy(() => retryImport(() => import('../pages/kintsugi-registrations')));
const BillingChangeRequestsPage = lazy(() =>
  retryImport(() => import('../pages/billing-change-requests/billing-change-requests-page'))
);
const KintsugiTasksPage = lazy(() => retryImport(() => import('../pages/kintsugi-tasks')));
const KintsugiCxPage = lazy(() => retryImport(() => import('../pages/kintsugi-cx')));
const CsmWorkspacePage = lazy(() => retryImport(() => import('../pages/csm')));

const OrganizationOverviewPage = lazy(() =>
  retryImport(() => import('../pages/organization/organization-overview-page'))
);
const OrganizationDetailsPage = lazy(() =>
  retryImport(() => import('../pages/organization/organization-details-page'))
);
const OrganizationHistoryPage = lazy(() => retryImport(() => import('../pages/organization/organization-history')));
const OrganizationSettingsPage = lazy(() =>
  retryImport(() => import('../pages/organization/organization-settings-page'))
);
const BillingAccessControlPage = lazy(() =>
  retryImport(() => import('../pages/billing-access-control/billing-access-control-page'))
);

const RegistrationListPage = lazy(() => retryImport(() => import('../pages/registrations/registrations-list')));
const RegistrationOverviewPage = lazy(() =>
  retryImport(() => import('../pages/registrations/registration-overview-page'))
);
const RegistrationDetailsPage = lazy(() =>
  retryImport(() => import('../pages/registrations/registration-details-page'))
);

const FilingsParent = lazy(() => retryImport(() => import('../pages/filings/filing-parent')));
const FilingsListPage = lazy(() => retryImport(() => import('../pages/filings/filing-list')));
const FilingsDetailsPage = lazy(() => retryImport(() => import('../pages/filings/filing-details-page')));

const AttachmentListPage = lazy(() => retryImport(() => import('../pages/attachments/attachments-list')));
const ComplianceDocumentsListPage = lazy(() =>
  retryImport(() => import('../pages/compliance-documents/compliance-documents-list'))
);
const ComplianceDocumentPreviewPage = lazy(() =>
  retryImport(() => import('../pages/compliance-documents/compliance-document-preview'))
);
const ImportsListPage = lazy(() => retryImport(() => import('../pages/imports/imports-list')));
const ConnectionsListPage = lazy(() => retryImport(() => import('../pages/connections/connections-list')));

const ReportsPage = lazy(() => retryImport(() => import('../pages/reports')));
const ToolsPage = lazy(() => retryImport(() => import('../pages/tools')));
const WorkloadPlanningHubPage = lazy(() => retryImport(() => import('../pages/tools/workload-planning')));
const VdaReportPage = lazy(() => retryImport(() => import('../pages/vda')));
const FilingProjectionPage = lazy(() => retryImport(() => import('../pages/tools/filing-projection')));
const WorkloadDetailPage = lazy(() => retryImport(() => import('../pages/tools/workload-detail')));
const EffortRatesPage = lazy(() => retryImport(() => import('../pages/tools/effort-rates')));
const ProposalsAllOrgsPage = lazy(() => retryImport(() => import('../pages/tools/proposals')));
const HealthCheckSignalCadencesPage = lazy(() =>
  retryImport(() => import('../pages/tools/health-check-signal-cadences'))
);
const QueuesToolsPage = lazy(() => retryImport(() => import('../pages/tools/queues')));
const MobileNotificationsToolsPage = lazy(() => retryImport(() => import('../pages/tools/mobile-notifications')));
const BulkReportRebuildPage = lazy(() => retryImport(() => import('../pages/tools/bulk-report-rebuild')));
const VdaConfigPage = lazy(() => retryImport(() => import('../pages/vda/config')));
const FilingAutomationPage = lazy(() => retryImport(() => import('../pages/filing-automation')));
const ConnecticutFilingAutomationPage = lazy(() => retryImport(() => import('../pages/filing-automation/connecticut')));
const PennsylvaniaFilingAutomationPage = lazy(() =>
  retryImport(() => import('../pages/filing-automation/pennsylvania'))
);
const TaxRulesLayout = lazy(() => retryImport(() => import('../pages/tax-rules/tax-rules-layout')));
const TaxRulesRatesListPage = lazy(() => retryImport(() => import('../pages/tax-rules/tax-rules-rates-list')));
const TaxRulesModifiersListPage = lazy(() => retryImport(() => import('../pages/tax-rules/tax-rules-modifiers-list')));
const TaxRulesRatesDetailPage = lazy(() => retryImport(() => import('../pages/tax-rules/tax-rules-rates-detail')));
const TaxRulesModifiersDetailPage = lazy(() =>
  retryImport(() => import('../pages/tax-rules/tax-rules-modifiers-detail'))
);
const TaxRulesRatesFormPage = lazy(() => retryImport(() => import('../pages/tax-rules/tax-rules-rates-form')));
const TaxRulesModifiersFormPage = lazy(() => retryImport(() => import('../pages/tax-rules/tax-rules-modifiers-form')));
const TaxRulesImportsPage = lazy(() => retryImport(() => import('../pages/tax-rules/tax-rules-imports-page')));
const TaxRulesLookupPage = lazy(() => retryImport(() => import('../pages/tax-rules/tax-rules-lookup-page')));
const TaxRulesSandboxPage = lazy(() => retryImport(() => import('../pages/tax-rules/tax-rules-sandbox-page')));
const TransactionsListPage = lazy(() => retryImport(() => import('../pages/transactions/transactions-list')));
const TransactionDetailsPage = lazy(() => retryImport(() => import('../pages/transactions/transaction-details')));
const TransactionProcessingTimelinePage = lazy(() =>
  retryImport(() => import('../pages/transactions/transaction-processing-timeline'))
);
const NexusPage = lazy(() => retryImport(() => import('../pages/nexus/nexus-list')));
const PhysicalPresencePage = lazy(() => retryImport(() => import('../pages/physical-presence/physical-presence-page')));
const PartnersListPage = lazy(() => retryImport(() => import('../pages/partners/partners-list')));
const PartnersDetailsPage = lazy(() => retryImport(() => import('../pages/partners/partners-details-page')));
const ResellersListPage = lazy(() => retryImport(() => import('../pages/resellers/resellers-list')));
const ResellerDetailsPage = lazy(() => retryImport(() => import('../pages/resellers/reseller-details-page')));
const PartnerHistoryPage = lazy(() => retryImport(() => import('@/pages/partners/partner-history')));
const PartnerBillingAccessPage = lazy(() => retryImport(() => import('@/pages/partners/partner-billing-access-page')));

const PartnerLegacyBillingAccessRedirect = () => {
  const { id } = useParams();
  return <Navigate to={`/partners/${id}/billing-access`} replace />;
};
const PartnerSettingsPage = lazy(() => retryImport(() => import('@/pages/partners/partner-settings-page')));
const NexusDetailsPage = lazy(() => retryImport(() => import('../pages/nexus/nexus-details-page')));
const VertexPartitionsPage = lazy(() =>
  retryImport(() =>
    import('../pages/vertex-partitions/vertex-partitions-page').then(module => ({
      default: module.VertexPartitionsPage,
    }))
  )
);

const fallback = () => <ErrorFallback />;
const errorBoundaryOptions = {
  fallback,
  onError: (error: unknown, componentStack: string) => {
    console.error('Route loading error:', error, componentStack);
    // Log chunk loading failures to Sentry with additional context
    if (error instanceof Error && error.message.includes('dynamically imported module')) {
      console.warn('Chunk loading failure detected - this may be due to rapid navigation');
    }
  },
  showDialog: false,
};

const AppRoutes = withErrorBoundary((): React.ReactNode => {
  const { user, accessToken } = useAuthInfo();

  useEffect(() => {
    if (user) {
      try {
        Sentry.setUser({ email: user.email, id: user.userId });
      } catch (error) {
        console.error('Failed to identify user:', error);
        Sentry.captureException(error);
      }

      storeApiToken(accessToken);
    }
  }, [user, accessToken]);

  return (
    <Router>
      <AuthProvider>
        <PrivacyProvider>
          <RegistrationsProvider>
            <FilingsProvider>
              <Routes>
                <Route path="/" element={<AppLayout />}>
                  <Route index element={<Navigate to="/kintsugi-filings" replace />} />
                  <Route path="kintsugi-filings" element={<KintsugiFilingsPage />} />
                  <Route path="kintsugi-registrations" element={<KintsugiRegistrationsPage />} />
                  {/* KA-1118: board replaces the legacy table when enabled; keep the table
                      route only as a fallback when the board flag is off. */}
                  {isKintsugiTasksBoardEnabled() ? (
                    <>
                      <Route path="kintsugi-tasks" element={<KintsugiTasksPage />} />
                      <Route
                        path="billing-change-requests"
                        element={<Navigate to="/kintsugi-tasks?type=BILLING_CHANGE_REQUEST" replace />}
                      />
                    </>
                  ) : (
                    <>
                      <Route path="billing-change-requests" element={<BillingChangeRequestsPage />} />
                    </>
                  )}
                  {isOrgHealthCheckEnabled() ? <Route path="kintsugi-cx" element={<KintsugiCxPage />} /> : null}
                  {isCsmWorkspaceEnabled() ? <Route path="csm" element={<CsmWorkspacePage />} /> : null}

                  {/* All org-specific routes - orgId required */}
                  <Route path="organization/:orgId" element={<OrganizationOverviewPage />} />
                  <Route path="organization/:orgId/details" element={<OrganizationDetailsPage />} />
                  <Route path="organization/:orgId/history" element={<OrganizationHistoryPage />} />
                  <Route path="organization/:orgId/settings" element={<OrganizationSettingsPage />} />
                  <Route path="billing-access-control/:orgId" element={<BillingAccessControlPage />} />
                  <Route path="registrations/:orgId" element={<RegistrationListPage />} />
                  <Route path="registrations/:orgId/:id" element={<RegistrationOverviewPage />} />
                  <Route path="registrations/:orgId/:id/details" element={<RegistrationDetailsPage />} />
                  <Route path="filings/:orgId" element={<FilingsParent />} />
                  <Route path="filings/:orgId/list" element={<FilingsListPage />} />
                  <Route path="filings/:orgId/:id" element={<FilingsDetailsPage />} />
                  <Route path="connections/:orgId" element={<ConnectionsListPage />} />
                  <Route path="attachments/:orgId" element={<AttachmentListPage />} />
                  <Route path="documents/:orgId" element={<ComplianceDocumentsListPage />} />
                  <Route path="documents/:orgId/:catalogType" element={<ComplianceDocumentPreviewPage />} />
                  <Route path="file-imports/:orgId" element={<ImportsListPage />} />
                  <Route path="transactions/:orgId" element={<TransactionsListPage />} />
                  <Route path="transactions/:orgId/:transactionId" element={<TransactionDetailsPage />} />
                  <Route
                    path="transactions/:orgId/:transactionId/processing-timeline"
                    element={<TransactionProcessingTimelinePage />}
                  />
                  <Route path="nexus/:orgId" element={<NexusPage />} />
                  <Route path="nexus/:orgId/:id" element={<NexusDetailsPage />} />
                  <Route path="physical-presence/:orgId" element={<PhysicalPresencePage />} />
                  <Route path="partners" element={<PartnersListPage />} />
                  <Route path="partners/:id" element={<PartnersDetailsPage />} />
                  <Route path="resellers" element={<ResellersListPage />} />
                  <Route path="resellers/:id" element={<ResellerDetailsPage />} />
                  <Route path="partners/:id/history" element={<PartnerHistoryPage />} />
                  <Route path="partners/:id/billing-access" element={<PartnerBillingAccessPage />} />
                  <Route path="partners/:id/country-settings" element={<PartnerLegacyBillingAccessRedirect />} />
                  <Route path="partners/:id/access-settings" element={<PartnerLegacyBillingAccessRedirect />} />
                  <Route path="partners/:id/settings" element={<PartnerSettingsPage />} />

                  <Route path="reports" element={<ReportsPage />} />
                  <Route path="vertex-partitions" element={<VertexPartitionsPage />} />
                  <Route path="reports/vda/:orgId" element={<ReportsVdaOrgRedirect />} />
                  <Route path="reports/vda" element={<ReportsVdaRedirect />} />
                  <Route path="tools" element={<ToolsPage />} />
                  <Route path="tools/workload-planning" element={<WorkloadPlanningHubPage />} />
                  <Route path="tools/vda/config" element={<VdaConfigPage />} />
                  <Route path="tools/vda/:orgId" element={<VdaReportPage />} />
                  <Route path="tools/vda" element={<VdaReportPage />} />
                  <Route path="tools/filing-projection" element={<FilingProjectionPage />} />
                  <Route path="tools/workload-detail" element={<WorkloadDetailPage />} />
                  <Route path="tools/effort-rates" element={<EffortRatesPage />} />
                  <Route path="tools/proposals" element={<ProposalsAllOrgsPage />} />
                  <Route path="tools/health-check-signal-cadences" element={<HealthCheckSignalCadencesPage />} />
                  <Route path="tools/queues" element={<QueuesToolsPage />} />
                  <Route path="tools/mobile-notifications" element={<MobileNotificationsToolsPage />} />
                  <Route path="tools/bulk-report-rebuild" element={<BulkReportRebuildPage />} />
                  <Route path="filing-automation" element={<FilingAutomationPage />} />
                  <Route path="filing-automation/connecticut" element={<ConnecticutFilingAutomationPage />} />
                  <Route path="filing-automation/pennsylvania" element={<PennsylvaniaFilingAutomationPage />} />
                  <Route path="tax-rules" element={<TaxRulesLayout />}>
                    <Route path="rates" element={<TaxRulesRatesListPage />} />
                    <Route path="rates/new" element={<TaxRulesRatesFormPage />} />
                    <Route path="rates/:id" element={<TaxRulesRatesDetailPage />} />
                    <Route path="rates/:id/edit" element={<TaxRulesRatesFormPage />} />
                    <Route path="modifiers" element={<TaxRulesModifiersListPage />} />
                    <Route path="modifiers/new" element={<TaxRulesModifiersFormPage />} />
                    <Route path="modifiers/:id" element={<TaxRulesModifiersDetailPage />} />
                    <Route path="modifiers/:id/edit" element={<TaxRulesModifiersFormPage />} />
                    <Route path="imports" element={<TaxRulesImportsPage />} />
                    <Route path="lookup" element={<TaxRulesLookupPage />} />
                    <Route path="sandbox" element={<TaxRulesSandboxPage />} />
                  </Route>
                  <Route path="*" element={<Navigate to="/" replace />} />
                </Route>
              </Routes>
            </FilingsProvider>
          </RegistrationsProvider>
        </PrivacyProvider>
      </AuthProvider>
    </Router>
  );
}, errorBoundaryOptions);

export default AppRoutes;
